import { Button, Modal, Tabs } from "@pickit/pickit-components";
import "./CapacityDetailModal.scss";
import { CapacityDetailModalProps } from "./types";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import "./i18n";
import {
  DashboardDropdownState,
  SlotPickers,
} from "reducers/types/dashboardSlotCapacityModal";
import { useCallback, useEffect, useMemo } from "react";
import moment from "moment";
import { CAPACITY_DETAIL_PARAM_TABS, DATE_FORMATS } from "utils/constants";
import { formatHHMM } from "utils/date";

const CapacityDetailModal = ({
  isOpen,
  dropdownState,
  slotDetail,
  toggleModal,
  getSlotDetail,
  setDropdownState,
}: CapacityDetailModalProps) => {
  const { t } = useTranslation("capacityDetailModal");
  const tabsOrder: DashboardDropdownState[] = useMemo(
    () => ["withTrx", "withoutTrx", "disconnected"],
    []
  );

  const initialTabIndex = useMemo(
    () => tabsOrder.indexOf(dropdownState),
    [dropdownState, tabsOrder]
  );

  const tabPickers: { [key in DashboardDropdownState]: SlotPickers[] } =
    useMemo(
      () => ({
        withTrx: slotDetail?.pickers.withTransaction,
        withoutTrx: slotDetail?.pickers.withoutTransaction,
        disconnected: slotDetail?.pickers.disconnected,
      }),
      [slotDetail?.pickers]
    );

  const maxAmount = Math.max(
    tabPickers?.withTrx?.length,
    tabPickers?.withoutTrx?.length,
    tabPickers?.disconnected?.length
  );

  const tabHeight = Math.min(292, Math.max(230, 40 + 36 * maxAmount));

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (isOpen) {
      params.set('modal', 'capacity-detail-modal');
      params.set('tab', CAPACITY_DETAIL_PARAM_TABS.withTrx);
    } else {
      params.delete('modal');
      params.delete('tab');
    }
    const paramsString = params.toString();
    const newUrl = paramsString ? `${window.location.pathname}?${paramsString}` : window.location.pathname;
    window.history.pushState({}, '', newUrl);
  }, [isOpen]);

  const resetUrl = useCallback((tab: DashboardDropdownState) => {
    const params = new URLSearchParams(window.location.search);
    params.set('tab', CAPACITY_DETAIL_PARAM_TABS[tab]);
    const paramsString = params.toString();
    const newUrl = paramsString ? `${window.location.pathname}?${paramsString}` : window.location.pathname;
    window.history.pushState({}, '', newUrl);
  }, []);
  

  return (
    <Modal
      width="960px"
      height="fit-content"
      isOpen={isOpen}
      onClose={() => toggleModal(false)}
    >
      <div className="capacity-slot-info-container">
        <h3 className="capacity-slot-region">{slotDetail?.region?.name}</h3>
        <h4 className="capacity-slot-workzone">{slotDetail?.workZone?.name}</h4>
        <p className="capacity-slot-time">
          {moment(slotDetail?.startAt).format(DATE_FORMATS.shortDate)}
        </p>
        <p className="capacity-slot-time">
          {slotDetail?.startAt && formatHHMM(slotDetail?.startAt)} a{" "}
          {slotDetail?.finishAt && formatHHMM(slotDetail?.finishAt)}
        </p>
      </div>
      <div className="tabs-container">
        <Tabs
          initialIndex={initialTabIndex}
          tabs={[
            { title: t("pickersWithTrx"), testId: "" },
            { title: t("pickersWithoutTrx"), testId: "" },
            { title: t("pickersDisconnected"), testId: "" },
          ]}
          size="minimal"
          width="180px"
          adjustableWidth
          onChange={(action, index) => {
            const selectedTab = tabsOrder[index as number];
            setDropdownState(selectedTab);
            action();
            resetUrl(selectedTab);
          }}          
        >
          <>
            {tabsOrder.map((tab) => (
              <div
                className="pickers-tab-container"
                style={{
                  height: tabHeight,
                }}
              >
                {tabPickers[tab]?.length !== 0 ? (
                  <>
                    <div className="pickers-tab-container-row capacity-pickers-info-titles">
                      <h3 className="pickers-tab-container-col-2">
                        {t("pickerId")}
                      </h3>
                      <h3 className="pickers-tab-container-col-3">
                        {t("pickerName")}
                      </h3>
                      <h3 className="pickers-tab-container-col-3">
                        {t("whatsapp")}
                      </h3>
                      {tab === "withTrx" && (
                        <>
                          <h3 className="pickers-tab-container-col-2">
                            {t("tripId")}
                          </h3>
                          <h3 className="pickers-tab-container-col-2">
                            {t("trxAmount")}
                          </h3>
                        </>
                      )}
                    </div>
                    <hr
                      className={classNames("pickers-separate", {
                        "pickers-separate-small": tab !== "withTrx",
                      })}
                    />
                    {tabPickers[tab]?.map((picker) => (
                      <>
                        <div className="pickers-tab-container-row capacity-pickers-info">
                          <p className="pickers-tab-container-col-2">
                            {picker.pickerId}
                          </p>
                          <p className="pickers-tab-container-col-3 picker-tab-name">
                            {picker.firstName} {picker.surname}
                          </p>
                          <div className="pickers-tab-container-col-3">
                            <a
                              href={`https://api.whatsapp.com/send/?phone=${
                                picker.phoneNumber
                              }&text=${t("contactText", {
                                name: `${picker.firstName}`,
                              })}&type=phone_number&app_absent=0`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {picker.phoneNumber}
                            </a>
                          </div>
                          {tab === "withTrx" && (
                            <>
                              <p className="pickers-tab-container-col-2">
                                {picker.tripCode}
                              </p>
                              <p className="pickers-tab-container-col-2">
                                {picker.pendingTransactions}/
                                {picker.totalTransactions}
                              </p>
                            </>
                          )}
                        </div>
                        <hr
                          className={classNames("pickers-separate", {
                            "pickers-separate-small": tab !== "withTrx",
                          })}
                        />
                      </>
                    ))}
                  </>
                ) : (
                  <div className="no-picker-container">
                    {t("noPickersText")}
                  </div>
                )}
              </div>
            ))}
          </>
        </Tabs>
        <div className="bottom-button-container">
          <div className="refresh-button">
            <Button
              label={t("global:update")}
              type="button"
              fullWidth
              size="medium"
              onClick={() => getSlotDetail(slotDetail.id)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CapacityDetailModal;
